import { useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { selectDataChangeRequests } from 'src/old/actions/dataChangeRequests/dataChangeRequestSelectors';
import { latamLocale } from 'src/shared/utils/currency';
import { any } from 'src/shared/utils/array';
import navConfig from '../Dashboard/navConfig';
import { filterHeroOnly } from '../Dashboard/utils';
import superAdminNavConfig from '../Dashboard/superAdminNavConfig';
import NavItems from './NavItems';
import ToggleButton from './ToggleButton';
import { fetchCompany, fetchDataChangeRequests } from '../../../../old/actions';
import { selectCompany } from '../../../../old/actions/company/companySelectors';
import { useGetUserHoldingQuery } from 'src/shared/api/holding';
import { useHasIntegration } from 'src/shared/hooks/useHasIntegration';
import { useIsHeroAdmin } from 'src/shared/hooks/useIsHeroAdmin';
import { List } from '@mui/material';
import { type SvgIconTypeMap } from '@mui/material';
import { type OverridableComponent } from '@mui/material/OverridableComponent';
import { selectUserRole } from '../../../../old/actions/session/sessionSelectors';
import { userRoles } from '../../../../old/reducers/sessionReducer';

export const PAYFLOW_LOGO_URL = {
  BASIC_HORIZONTAL: '/images/logos/Payflow_logo_Horizontal.svg',
  BASIC_VERTICAL: '/images/logos/Payflow_logo_Vertical.svg',
  WHITE_HORIZONTAL: '/images/logos/Payflow_logo_Horizontal_White.svg'
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex'
  },
  navigation: {
    overflow: 'auto',
    padding: 0,
    flexGrow: 1,
    backgroundColor: '#3859E3',
    transition: 'width 250ms ease-in-out',
    overflowX: 'hidden',
    overflowY: 'scroll',
    MsOverflowStyle: 'hidden',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  logo: {
    transition: 'all .125s ease-in-out',
    height: '40px',
    margin: '24px auto 16px',
    display: 'block',
    opacity: 1
  },
  logoChanging: {
    opacity: 0
  }
}));

export type NavBarItem = {
  title?: string;
  badge?: number;
  href?: string;
  action?: string;
  icon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>>;
  items?: NavBarItem[];
  isExternal?: boolean;
  heroOnly?: boolean;
};

export type NavBarChildItem = Omit<NavBarItem, 'items'>;

const NavbarContent = ({ userRole, menuOpen, ...rest }: { userRole: string; menuOpen: boolean; rest: any }) => {
  const classes = useStyles({ collapsed: !menuOpen });
  const dispatch = useDispatch();
  const company = useSelector(selectCompany);
  const isSuper = useSelector(selectUserRole) === userRoles.ADMIN;
  const dataChangeRequests = useSelector(selectDataChangeRequests);
  const { t } = useTranslation(['nav'], { useSuspense: false });
  const location = useLocation();
  const isSuperAdmin = location.pathname.startsWith('/super');
  const { data: holding } = useGetUserHoldingQuery(undefined, { skip: isSuperAdmin });
  const hasIntegration = useHasIntegration(isSuperAdmin);
  const { isHeroAdmin, isError: isScopeError } = useIsHeroAdmin();
  const useNavConfig: NavBarItem[] = isSuperAdmin
    ? superAdminNavConfig(t)
    : filterHeroOnly(
        navConfig(t, isSuper, company, holding, dataChangeRequests?.meta?.totalCompanyPendingRequests, hasIntegration),
        // /me failing must not silently demote a Hero admin; the API enforces the real scope.
        isHeroAdmin || isScopeError
      );
  const [logoUrl, setLogoUrl] = useState(PAYFLOW_LOGO_URL.BASIC_HORIZONTAL);
  const [isLogoTransitioning, setIsLogoTransitioing] = useState(false);
  // Helper to check if any child href matches current path
  const isChildRouteActive = (item: NavBarItem | NavBarChildItem): boolean => {
    if ('items' in item && item.items) {
      return item.items.some(isChildRouteActive);
    }
    if (item.href) {
      return location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    }
    return false;
  };

  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    useNavConfig.findIndex(item => isChildRouteActive(item)) ?? null
  );

  useEffect(() => {
    if (!isSuperAdmin) {
      // @ts-expect-error legacy thunk dispatch typing mismatch
      dispatch(fetchCompany());
    }
    // eslint-disable-next-line
  }, [isSuperAdmin]);

  useEffect(() => {
    if (latamLocale.includes(company?.locale) && !any(dataChangeRequests)) {
      void fetchDataChangeRequests(company?.locale)()(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    setIsLogoTransitioing(true);

    setTimeout(() => {
      setLogoUrl(menuOpen ? PAYFLOW_LOGO_URL.BASIC_HORIZONTAL : PAYFLOW_LOGO_URL.BASIC_VERTICAL);
      setIsLogoTransitioing(false);
    }, 125);
  }, [menuOpen]);
  return (
    <>
      <div {...rest} className={clsx(classes.root)} data-testid="navbar-wrapper">
        <nav className={classes.navigation}>
          <img className={clsx(classes.logo)} src={logoUrl} alt="Payflow" />
          <List>
            {useNavConfig.map((item, index) => (
              <NavItems
                key={`nav-item-${index}-${item.title ?? 'untitled'}`}
                item={item}
                index={index}
                expandedIndex={expandedIndex}
                setExpandedIndex={setExpandedIndex}
              />
            ))}
          </List>
        </nav>
        <Divider />
      </div>
      <ToggleButton menuOpen={menuOpen} />
    </>
  );
};

NavbarContent.propTypes = {
  userRole: PropTypes.oneOf(['ADMIN', 'USER']),
  menuOpen: PropTypes.bool
};
export default NavbarContent;
